"use client";

import { useEffect, useRef } from "react";
import { getAnalytics } from "../analytics-runtime";

export function AnalyticsPageView({ shouldFlush }: AnalyticsPageViewProps): null {
	const lastTrackedUrl = useRef<string | null>(null);

	useEffect(() => {
		const trackIfChanged = () => {
			if (lastTrackedUrl.current !== window.location.href) {
				lastTrackedUrl.current = window.location.href;
				trackPageView(shouldFlush);
			}
		};

		trackIfChanged();

		window.addEventListener("popstate", trackIfChanged);

		// Patch pushState and replaceState to track SPA navigation
		// This is necessary for the tracker to be framework-agnostic
		const originalPushState = window.history.pushState.bind(window.history);
		const originalReplaceState = window.history.replaceState.bind(window.history);

		window.history.pushState = (data: unknown, unused: string, url?: string | URL | null) => {
			originalPushState(data, unused, url);
			window.dispatchEvent(new Event("pushstate"));
		};

		window.history.replaceState = (data: unknown, unused: string, url?: string | URL | null) => {
			originalReplaceState(data, unused, url);
			window.dispatchEvent(new Event("replacestate"));
		};

		window.addEventListener("pushstate", trackIfChanged);
		window.addEventListener("replacestate", trackIfChanged);

		return () => {
			window.removeEventListener("popstate", trackIfChanged);
			window.removeEventListener("pushstate", trackIfChanged);
			window.removeEventListener("replacestate", trackIfChanged);
			window.history.pushState = originalPushState;
			window.history.replaceState = originalReplaceState;
		};
	}, [shouldFlush]);

	return null;
}

export type AnalyticsPageViewProps = {
	shouldFlush?: (pathname: string) => boolean;
};

function trackPageView(shouldFlush?: (pathname: string) => boolean) {
	try {
		const pathname = window.location.pathname;
		const search = window.location.search;
		const url = window.location.href;

		const analytics = getAnalytics();

		analytics.trackPageView(pathname, {
			path: pathname + search,
			url: url,
			title: document.title,
		});

		if (shouldFlush?.(pathname)) {
			analytics.flush();
		}
	} catch (error) {
		console.error("Analytics tracking error:", error);
	}
}
